import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import Pagination from 'react-ultimate-pagination-bootstrap-4';
import {compose} from 'redux';
import queryString from 'query-string';
import moment from 'moment';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';

import FontAwesome from 'utils/FA';
import { titleize } from 'utils/strings';
import { formatCurrency, formatDate } from 'utils';
import { duckify, filterDuck, singleDuck, dependentDuck } from 'utils/duckify';
import {
  billingAccountContacts, billingAccounts,
  billingCategoryGroups as billingCategoriesDuck,
  bulkAddTransactionJobs,
  fields as fieldsDuck,
  flashMessages,
  modals,
  users,
  customers as customersDuck,
} from 'ducks';

import ContactList from 'components/ContactList/ContactList';
import Filters from 'components/Filters/Filters';
import SummaryTotalsHeader from 'components/ContactList/SummaryTotalsHeader';

import withFiltering from 'hocs/withFiltering';
import withPagination from 'hocs/withPagination';

import queryBuilder from '../../utils/queryBuilder'
import withSchoolYear, {defaultBillingSchoolYear} from '../../hocs/withSchoolYear';
import withSorting from 'hocs/withSorting';
import './styles/AccountsListPage.scss';
import useBulkActions from "../../hooks/useBulkActions";
import BulkActions from "../ContactList/BulkActions/BulkActions";
import Edit from "../ContactList/BulkActions/Edit";
import AddCharge from "../ContactList/BulkActions/AddCharge";
import AddCredit from "../ContactList/BulkActions/AddCredit";
import CommunicationMenu from '../ContactList/BulkActions/CommunicationMenu'
import FlashRoot from "../FlashMessage/FlashRoot";
import LinkButton from "../LinkButton/LinkButton";
import {
  bulkCheckboxColumn,
  currencyColumn,
  dateColumn,
  defaultColumn,
  linkColumn
} from '../ContactList/ColumnDefinitions';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
import BulkAction from '../ContactList/BulkActions/BulkAction';

const customerDuck = singleDuck(customersDuck, 'current');

const AccountsListPage = ({
  contacts,
  fields,
  additionalFields,
  billingCategories,
  filters,
  onSort,
  page,
  setFilters,
  setPage,
  sort,
  addTransaction,
  showFlash,
  showModal,
  hideModal,
  dispatch,
  currentUser,
}) => {
  const {
    trackedItems,
    selectionModeCount,
    promptSelectAll,
    setPromptSelectAll,
    selectionMode,
    togglePageTracked,
    toggleTrackedItem,
    itemIsSelected,
    selectAllItems
  } = useBulkActions(contacts);

  const columns = [
    bulkCheckboxColumn({
      value: 'id',
      promptSelectAll,
      togglePageTracked,
      selectionMode,
      itemIsSelected,
      toggleTrackedItem
    }),
    linkColumn({
      header: 'Name',
      sortName: 'display_name',
      value: (contact) => (contact.display_name),
      link: (contact) => `/contacts/${contact.id}/billing`
    }),
    defaultColumn({
      header: 'AutoPay',
      sortName: 'billing_autopay_enabled',
      type: 'icon',
      value: (contact, key) => (
        <td className='contact-list-react-type-icon' key={key}>
          {
            contact.billing_autopay_enabled === true
              ? <FontAwesome className='autopay-check green' name='check'/>
              : ''
          }
        </td>
      )
    }),
    defaultColumn({
      header: 'Payment Method',
      sortName: 'billing_payment_method',
      value: (contact) => (
        <td>
          {titleize(contact.payment_method)}
        </td>
      )
    }),
    dateColumn({
      header: 'Overdue Date',
      sortName: 'billing_overdue_at',
      value: (contact) => {
        const isToday = moment(new Date()).isSame(contact.billing_overdue_at, 'day');
        const daysAgo = moment(contact.billing_overdue_at).fromNow(true);

        const overdueDate = contact.billing_overdue_at ? (
          <span className='billing-overdue-date'>
            {formatDate(contact.billing_overdue_at)}
          </span>
        ) : null;

        const overdueDays = contact.billing_overdue_at && !isToday ? (
          <span className='billing-overdue-days'>
            {contact.billing_overdue_at && !isToday ? `(${daysAgo})` : null}
          </span>
        ) : null;

        return (
          <td className='contact-list-react-type-date'>
            {overdueDate}
            &nbsp;
            {overdueDays}
          </td>
        );
      }
    }),
    dateColumn({
      header: 'Next Date',
      value: 'next_account_billing_date'
    }),
    currencyColumn({
      header: 'Next Amount',
      sortName: 'next_account_balance',
      value: 'next_account_balance'
    }),
    currencyColumn({
      header: 'Remaining Balance',
      sortName: 'total_account_balance',
      value: 'total_account_balance'
    }),
    currencyColumn({
      header: 'Current Balance',
      sortName: 'current_account_balance',
      value: (contact) => (
        <td className='contact-list-react-type-currency'>
          {
            contact.billing_overdue_at ? (
              <span>
                <FontAwesome icon={faExclamationTriangle} className='red' postNbsp/>
              </span>
            ) : null
          }

          <span>
            {formatCurrency(contact.current_account_balance)}
          </span>
        </td>
      )
    }),
    defaultColumn({
      header: 'Autopay Billing Days',
      value: 'autopay_billing_days',
      sortName: 'autopay_billing_days',
      columnClassNames: 'contact-list-react-type-number'
    }),
    defaultColumn({
      header: 'Actions',
      sortName: 'action_name',
      columnClassNames: 'account_actions',
      value: (contact) => (
        <td className='contact-list-react-type-currency'>
          {
            contact.is_account_active  ?
              <span className='deactivate_account_button' onClick={() => activateAccount(contact.billing_account_id, false)}>
                Make Inactive
              </span>
              : <span className='activate_account_button' onClick={() => activateAccount(contact.billing_account_id, true)}>
                  Make Active
               </span>
          }
        </td>
      )
    })
  ];

  const totalPages = Math.ceil(contacts.total / contacts.perPage);

  const summaryColumns = [
    {name: 'next_account_balance', publicName: 'Next Amount'},
    {name: 'total_account_balance', publicName: 'Remaining Amount'},
    {name: 'current_account_balance', publicName: 'Current Amount'}
  ];

  const filterFields = [
    {
      solr_prompt: 'is_account_active',
      display_name: 'Billing Account Active',
      options: [['Yes', 'true'], ['No', 'false']]
    }
  ].concat(fields.concat(additionalFields));

  const defaultSelected = {
    is_account_active: ['true']
  };

  const activateAccount = (billingAccountId, isActive) => {
    dispatch(billingAccounts.actions.update({ id: billingAccountId, active: isActive })).then((response) => {
      const isActiveMessage = isActive ? 'reactivated' : 'deactivated';
      const message = `This account is being ${isActiveMessage} in the background. Please wait and check back later`;
      showFlash({ message });
    }).catch((response) => {
      if (response.billing_account !== undefined && response.billing_account.errors !== undefined
        && response.billing_account.errors.active !== undefined &&
        response.billing_account.errors.active[0] !== undefined) {
        showFlash({ type: 'danger', message: response.billing_account.errors.active[0] });
      } else {
        Utility.displayFlashMessages({ error: 'Sorry, something went wrong.' });
      }
    });
  };

  return (
    <div className='billing-students-list'>
      <FlashRoot />
      <div className='contact-list-react-page-header'>
        <div className='contact-list-react-page-title accounts-space-between'>
          <h2 className='inline'>
            Accounts
          </h2>

          <BulkActions
            search={contacts.search}
            contactTotal={contacts.total}
            filterType={selectionMode}
            filterContactIds={trackedItems}
            showFlash={(props) => showFlash(props)}
            showModal={(name, props) => showModal(name, props)}
            hideModal={() => hideModal()}
          >
            <BulkAction title='Export' icon={faSignOut}>
              <BulkExportAction
                type='AccountReportJob'
                location={location}
                url='/api/billing_account_contacts/export.csv'
              />
              <BulkExportAction
                title='Export to PDF'
                type='AccountReportJob'
                location={location}
                url='/api/billing_account_contacts/export.pdf'
              />
            </BulkAction>
            <CommunicationMenu disabled={!contacts.ready || selectionModeCount() === 0} active={currentUser.communications_permitted} selectableContactTypes={['selected']} />
            <Edit disabled={!contacts.ready || !billingCategories.ready || selectionModeCount() === 0}>
              <AddCharge
                addTransaction={addTransaction}
                billingCategories={billingCategories}
                count={selectionModeCount()}
                jobType={'account'}
              />
              <AddCredit
                addTransaction={addTransaction}
                billingCategories={billingCategories}
                count={selectionModeCount()}
                jobType={'account'}
              />
            </Edit>
          </BulkActions>
        </div>

        <div>
          <SummaryTotalsHeader apiPath='api/billing/accounts/summary' columns={summaryColumns}/>

          <div className='accounts-space-between'>
            <div>
              {promptSelectAll && (
                <div className={'contact-list-react-prompt-select-all'}>
                  <span>You have selected <strong>{selectionModeCount()}</strong> contacts.</span>
                  &nbsp;
                  <LinkButton
                    onClick={selectAllItems}
                    content={<span>Select all <strong>{contacts.total}</strong> accounts across all pages.</span>}
                  />
                </div>
              )}

              {(filterFields.length) > 0 && (
                <Filters
                  fields={filterFields}
                  selectCount={selectionModeCount()}
                  count={contacts.total}
                  selected={{...defaultSelected, ...filters}}
                  submitFilters={setFilters}
                  itemsName='Accounts'
                />
              )}
            </div>

            {totalPages > 1 && (
              <div className='contact-list-react-pagination accounts-pagination'>
                <div className='pagination pagination-right'>
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    hideFirstAndLastPageLinks
                    onChange={(newPage) => {
                      setPage(newPage);
                      setPromptSelectAll(false);
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          <ContactList
            columns={columns}
            contacts={contacts}
            onSort={onSort}
            sort={sort}
          />
        </div>
      </div>
    </div>
  );
};

AccountsListPage.propTypes = {
  /** The list of contacts */
  contacts: PropTypes.arrayOf(PropTypes.object),

  /** The default fields that are available to filter by */
  fields: PropTypes.arrayOf(PropTypes.object),

  /** Additional fields that are available to filter by */
  additionalFields: PropTypes.arrayOf(PropTypes.object),

  /** The filters selected for this page */
  filters: PropTypes.shape({
    [PropTypes.string]: PropTypes.arrayOf(PropTypes.string)
  }),

  /** Used to generate a link to sort by a given column */
  onSort: PropTypes.func,

  /** The current page */
  page: PropTypes.number.isRequired,

  /** Sets filters to a given filterset */
  setFilters: PropTypes.func,

  /** Sets page to a given page */
  setPage: PropTypes.func,

  /** The sort selected for this page */
  sort: PropTypes.shape({
    column: PropTypes.string,
    order: PropTypes.string
  }),

  /** Hides all modals */
  hideModal: PropTypes.func,
  /** Shows a flash message */
  showFlash: PropTypes.func,

  /** Shows a modal */
  showModal: PropTypes.func,
  /** Adds a transaction */
  addTransaction: PropTypes.func,

  /** add React dispatch */
  dispatch: PropTypes.func
};

AccountsListPage.defaultProps = {
  contacts: [],
  fields: [],
  additionalFields: [],
  filters: {},
  sort: {},

  onSort: () => false,
  setFilters: () => false,
  setPage: () => false,
  showFlash: () => false,
  showModal: () => false,
  hideModal: () => false,
  addTransaction: () => false,
  dispatch: () => false
};

const mapDucksToProps = ({location, sortString}) => {
  const query = queryString.parse(location.search);
  query.sort = sortString;

  return {
    billingCategories: filterDuck(billingCategoriesDuck, {active: true, non_contact_only: true}),
    contacts: dependentDuck(customerDuck,
      ({ value }) => queryBuilder(
        defaultBillingSchoolYear({ customer: value }), billingAccountContacts, location
      )),
    fields: filterDuck(fieldsDuck, {
      solr_prompt: ['billing_autopay_enabled', 'billing_overdue', 'billing_payment_method']
    }),
    additionalFields: filterDuck(fieldsDuck, {for: 'billing'}),
    currentUser: singleDuck(users, 'current'),
    customer: customerDuck,
  };
};

const mapDispatchToProps = (dispatch) => ({
  showModal: (name, props) => dispatch(modals.actions.show(name, props)),
  addTransaction: (transaction) => dispatch(bulkAddTransactionJobs.actions.create(transaction)),
  showFlash: (props) => dispatch(flashMessages.actions.show(props)),
  hideModal: () => dispatch(modals.actions.hide())
});

export default compose(
  withRouter,
  withFiltering,
  withPagination,
  withSorting({column: 'display_name', order: 'desc'}),
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps),
  withSchoolYear(defaultBillingSchoolYear)
)(AccountsListPage);
